'use client';

import { useState, useEffect, useCallback, Suspense, useRef } from 'react';
import api from '@/lib/api';
import {
  Pose, PoseTransferJobResponse, PoseTransferQueueStats,
  HealthResponse, JobResponse
} from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import StatusBar from '@/components/StatusBar';
import { LazyImage } from '@/components/LazyImage';

export default function PosePage() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
      <PoseContent />
    </Suspense>
  );
}

function PoseContent() {
  const [poses, setPoses] = useState<Pose[]>([]);
  const [transferJobs, setTransferJobs] = useState<PoseTransferJobResponse[]>([]);
  const transferJobsRef = useRef<PoseTransferJobResponse[]>([]);
  const [stats, setStats] = useState<PoseTransferQueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  // Pose extraction state
  const [extractSourceJobId, setExtractSourceJobId] = useState<string | null>(null);
  const [extractSourceImage, setExtractSourceImage] = useState<string | null>(null);
  const [extractPoseName, setExtractPoseName] = useState('');
  const [extractPoseDescription, setExtractPoseDescription] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);

  // Pose transfer state
  const [transferSourceJobId, setTransferSourceJobId] = useState<string | null>(null);
  const [transferSourceImage, setTransferSourceImage] = useState<string | null>(null);
  const [selectedPoseId, setSelectedPoseId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState('');
  const [width, setWidth] = useState(512);
  const [height, setHeight] = useState(512);
  const [steps, setSteps] = useState(20);
  const [cfg, setCfg] = useState(7.0);
  const [controlnetStrength, setControlnetStrength] = useState(0.8);
  const [seed, setSeed] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState<'extract' | 'transfer' | null>(null);
  const [galleryJobs, setGalleryJobs] = useState<JobResponse[]>([]);

  // Selected result modal
  const [selectedJob, setSelectedJob] = useState<PoseTransferJobResponse | null>(null);

  // Refs for scrolling
  const inProgressRef = useRef<HTMLElement>(null);
  const resultsRef = useRef<HTMLElement>(null);

  // Track job IDs for completion detection
  const prevCompletedCountRef = useRef<number>(0);

  const toast = useToast();
  const { confirm } = useConfirm();

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, posesRes, jobsRes] = await Promise.all([
        api.getHealth(),
        api.getPoseTransferQueueStats(),
        api.listPoses(),
        api.listPoseTransferJobs(undefined, 50, 0),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setPoses(posesRes);
      setTransferJobs(jobsRes);
      transferJobsRef.current = jobsRes;
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchGalleryImages = useCallback(async () => {
    try {
      const images = await api.listJobs('completed', 100, 0);
      setGalleryJobs(images);
    } catch (error) {
      console.error('Failed to fetch gallery images:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = transferJobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Detect job completion and scroll to results
  useEffect(() => {
    const completedCount = transferJobs.filter(j => j.status === 'completed').length;
    if (completedCount > prevCompletedCountRef.current && prevCompletedCountRef.current > 0) {
      // A new job completed - show notification and scroll
      toast.success('Pose transfer complete!', {
        action: {
          label: 'View',
          onClick: () => {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
      // Auto-scroll to results
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
    prevCompletedCountRef.current = completedCount;
  }, [transferJobs, toast]);

  const handleSelectImage = async (mode: 'extract' | 'transfer') => {
    await fetchGalleryImages();
    setShowImagePicker(mode);
  };

  const handleImageSelected = (job: JobResponse, mode: 'extract' | 'transfer') => {
    if (mode === 'extract') {
      setExtractSourceJobId(job.job_id);
      setExtractSourceImage(job.images[0]);
    } else {
      setTransferSourceJobId(job.job_id);
      setTransferSourceImage(job.images[0]);
    }
    setShowImagePicker(null);
  };

  const handleExtractPose = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!extractSourceJobId || !extractPoseName.trim()) {
      toast.error('Please select a source image and enter a pose name');
      return;
    }

    setIsExtracting(true);
    try {
      await api.extractPose({
        source_job_id: extractSourceJobId,
        name: extractPoseName.trim(),
        description: extractPoseDescription.trim() || undefined,
      });
      toast.success('Pose extracted successfully!');
      setExtractPoseName('');
      setExtractPoseDescription('');
      setExtractSourceJobId(null);
      setExtractSourceImage(null);
      await fetchData();
    } catch (error) {
      console.error('Failed to extract pose:', error);
      toast.error('Failed to extract pose: ' + (error as Error).message);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferSourceJobId || !selectedPoseId || !prompt.trim()) {
      toast.error('Please select a source image, a pose, and enter a prompt');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createPoseTransferJob({
        prompt: prompt.trim(),
        source_job_id: transferSourceJobId,
        pose_id: selectedPoseId,
        width,
        height,
        steps,
        cfg,
        controlnet_strength: controlnetStrength,
        seed,
      });
      toast.success('Job submitted! Processing...', {
        action: {
          label: 'View Progress',
          onClick: () => {
            inProgressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
      setPrompt('');
      await fetchData();
      // Scroll to in-progress section
      setTimeout(() => {
        inProgressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePose = async (poseId: number) => {
    const confirmed = await confirm({
      title: 'Delete Pose',
      message: 'Delete this pose from your library? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await api.deletePose(poseId);
        if (selectedPoseId === poseId) setSelectedPoseId(null);
        await fetchData();
        toast.success('Pose deleted');
      } catch (error) {
        console.error('Failed to delete pose:', error);
        toast.error('Failed to delete pose: ' + (error as Error).message);
      }
    }
  };

  const handleToggleFavorite = async (pose: Pose) => {
    try {
      await api.updatePose(pose.id, { is_favorite: !pose.is_favorite });
      await fetchData();
    } catch (error) {
      console.error('Failed to update pose:', error);
    }
  };

  const handleCancelJob = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelPoseTransferJob(jobId, force);
      await fetchData();
      toast.success('Job cancelled');
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    const confirmed = await confirm({
      title: 'Delete Result',
      message: 'Delete this pose transfer result? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        await api.deletePoseTransferJob(jobId);
        await fetchData();
        toast.success('Job deleted');
      } catch (error) {
        console.error('Failed to delete job:', error);
        toast.error('Failed to delete job: ' + (error as Error).message);
      }
    }
  };

  const activeJobs = transferJobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = transferJobs.filter(j => j.status === 'completed');
  const failedJobs = transferJobs.filter(j => j.status === 'failed');
  const selectedPose = selectedPoseId ? poses.find(p => p.id === selectedPoseId) : null;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">Pose Transfer</h1>
            <p className="text-[var(--text-muted)]">Extract poses and transfer them to new images</p>
          </div>

          {/* Two-column layout for extraction and library */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pose Extraction */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4">Extract Pose</h2>
              <form onSubmit={handleExtractPose} className="space-y-4">
                {/* Source Image Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">Source Image</label>
                  <div className="flex items-start gap-4">
                    {extractSourceImage ? (
                      <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-[var(--bg-primary)] flex-shrink-0 group">
                        <img
                          src={api.getThumbnailUrl(extractSourceImage)}
                          alt="Source"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => { setExtractSourceJobId(null); setExtractSourceImage(null); }}
                          className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors cursor-pointer"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSelectImage('extract')}
                        className="w-24 h-24 rounded-lg border-2 border-dashed border-[var(--border-subtle)] hover:border-[var(--accent)] flex flex-col items-center justify-center gap-1 text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        <span className="text-xs">Select</span>
                      </button>
                    )}
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={extractPoseName}
                        onChange={(e) => setExtractPoseName(e.target.value)}
                        placeholder="Pose name"
                        className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none text-sm"
                      />
                      <input
                        type="text"
                        value={extractPoseDescription}
                        onChange={(e) => setExtractPoseDescription(e.target.value)}
                        placeholder="Description (optional)"
                        className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isExtracting || !extractSourceJobId || !extractPoseName.trim()}
                  className="w-full py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm"
                >
                  {isExtracting ? 'Extracting...' : 'Extract Pose'}
                </button>
              </form>
            </div>

            {/* Pose Library */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Pose Library</h2>
                <span className="text-xs text-[var(--text-muted)]">{poses.length} poses</span>
              </div>

              {poses.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <svg className="w-12 h-12 text-[var(--text-muted)] mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  <p className="text-sm text-[var(--text-muted)]">No poses yet</p>
                  <p className="text-xs text-[var(--text-muted)]">Extract poses from your images</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {poses.map((pose) => (
                    <div
                      key={pose.id}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                        selectedPoseId === pose.id
                          ? 'ring-2 ring-[var(--accent)]'
                          : 'hover:ring-2 hover:ring-[var(--border-subtle)]'
                      }`}
                      onClick={() => setSelectedPoseId(pose.id)}
                    >
                      <LazyImage
                        src={api.getPoseImageUrl(pose.skeleton_image)}
                        alt={pose.name}
                        className="w-full h-full"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2">
                          <p className="text-white text-xs truncate">{pose.name}</p>
                        </div>
                        {/* Actions */}
                        <div className="absolute top-1 right-1 flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleFavorite(pose); }}
                            className={`p-1 rounded transition-colors ${
                              pose.is_favorite ? 'text-yellow-400' : 'text-white/60 hover:text-yellow-400'
                            }`}
                          >
                            <svg className="w-4 h-4" fill={pose.is_favorite ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePose(pose.id); }}
                            className="p-1 rounded text-white/60 hover:text-red-400 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {/* Selection indicator */}
                      {selectedPoseId === pose.id && (
                        <div className="absolute top-1 left-1 w-5 h-5 bg-[var(--accent)] rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pose Transfer Form */}
          <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">Pose Transfer</h2>
            <form onSubmit={handleTransfer} className="space-y-6">
              {/* Source and Pose Selection Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Source Person */}
                <div>
                  <label className="block text-sm font-medium mb-2">Source Person</label>
                  <div className="flex items-start gap-4">
                    {transferSourceImage ? (
                      <div className="relative w-28 h-28 rounded-lg overflow-hidden bg-[var(--bg-primary)] flex-shrink-0 group">
                        <img
                          src={api.getThumbnailUrl(transferSourceImage)}
                          alt="Source Person"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => { setTransferSourceJobId(null); setTransferSourceImage(null); }}
                          className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSelectImage('transfer')}
                        className="w-28 h-28 rounded-lg border-2 border-dashed border-[var(--border-subtle)] hover:border-[var(--accent)] flex flex-col items-center justify-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer"
                      >
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        <span className="text-xs">Select Person</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Target Pose */}
                <div>
                  <label className="block text-sm font-medium mb-2">Target Pose</label>
                  {selectedPose ? (
                    <div className="relative w-28 h-28 rounded-lg overflow-hidden bg-[var(--bg-primary)]">
                      <img
                        src={api.getPoseImageUrl(selectedPose.skeleton_image)}
                        alt={selectedPose.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedPoseId(null)}
                        className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors cursor-pointer"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/60">
                        <p className="text-white text-xs truncate">{selectedPose.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-28 h-28 rounded-lg border-2 border-dashed border-[var(--border-subtle)] flex flex-col items-center justify-center gap-2 text-[var(--text-muted)]">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                      <span className="text-xs text-center">Select from library above</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium mb-2">Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the person/scene (e.g., 'a woman in a red dress, professional photo')"
                  className="w-full px-4 py-3 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                  rows={3}
                />
              </div>

              {/* ControlNet Strength */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Pose Strength: {controlnetStrength.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={controlnetStrength}
                  onChange={(e) => setControlnetStrength(parseFloat(e.target.value))}
                  className="w-full accent-[var(--accent)]"
                />
                <div className="flex justify-between text-xs text-[var(--text-muted)] mt-1">
                  <span>0.0 - Ignore pose</span>
                  <span>1.0 - Strict pose matching</span>
                </div>
              </div>

              {/* Settings Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Width</label>
                  <select
                    value={width}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                  >
                    <option value={512}>512</option>
                    <option value={768}>768</option>
                    <option value={1024}>1024</option>
                    <option value={1280}>1280</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Height</label>
                  <select
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none cursor-pointer"
                  >
                    <option value={512}>512</option>
                    <option value={768}>768</option>
                    <option value={1024}>1024</option>
                    <option value={1280}>1280</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">Steps</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={steps}
                    onChange={(e) => setSteps(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-muted)] mb-1">CFG (1-7)</label>
                  <input
                    type="number"
                    min={1}
                    max={7}
                    step={0.5}
                    value={cfg}
                    onChange={(e) => setCfg(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>
              </div>

              {/* Seed */}
              <div className="w-1/2">
                <label className="block text-xs text-[var(--text-muted)] mb-1">Seed (optional)</label>
                <input
                  type="number"
                  value={seed ?? ''}
                  onChange={(e) => setSeed(e.target.value ? Number(e.target.value) : null)}
                  placeholder="Random"
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || !transferSourceJobId || !selectedPoseId || !prompt.trim()}
                className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? 'Creating...' : 'Generate with Pose'}
              </button>
            </form>
          </div>

          {/* Queue Stats */}
          {stats && (stats.pending > 0 || stats.processing > 0) && (
            <div className="bg-[var(--bg-elevated)] rounded-lg p-4 flex items-center gap-6">
              {stats.processing > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                  <span className="text-sm">{stats.processing} processing</span>
                </div>
              )}
              {stats.pending > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-sm">{stats.pending} in queue</span>
                </div>
              )}
            </div>
          )}

          {/* Active Jobs */}
          {activeJobs.length > 0 && (
            <section ref={inProgressRef} className="space-y-4">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                In Progress
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {activeJobs.map((job) => (
                  <PoseTransferJobCard
                    key={job.job_id}
                    job={job}
                    onCancel={handleCancelJob}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Failed Jobs */}
          {failedJobs.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-red-400 uppercase tracking-wider">
                Failed
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {failedJobs.map((job) => (
                  <PoseTransferJobCard
                    key={job.job_id}
                    job={job}
                    onDelete={handleDeleteJob}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Completed Results */}
          <section ref={resultsRef} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                Results
              </h2>
              {completedJobs.length > 0 && (
                <span className="text-xs text-[var(--text-muted)]">
                  {completedJobs.length} images
                </span>
              )}
            </div>

            {completedJobs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </div>
                <p className="text-[var(--text-muted)]">No pose transfers yet</p>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Extract a pose and apply it to a new image
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {completedJobs.map((job) => (
                  <PoseTransferJobCard
                    key={job.job_id}
                    job={job}
                    onSelect={setSelectedJob}
                    onDelete={handleDeleteJob}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Image Picker Modal */}
      {showImagePicker && (
        <ImagePickerModal
          images={galleryJobs}
          onSelect={(job) => handleImageSelected(job, showImagePicker)}
          onClose={() => setShowImagePicker(null)}
        />
      )}

      {/* Result Modal */}
      {selectedJob && selectedJob.status === 'completed' && (
        <PoseTransferResultModal
          job={selectedJob}
          jobs={completedJobs}
          poses={poses}
          onClose={() => setSelectedJob(null)}
          onNavigate={setSelectedJob}
          onDelete={handleDeleteJob}
        />
      )}
    </div>
  );
}

// Pose Transfer Job Card Component
function PoseTransferJobCard({
  job,
  onSelect,
  onCancel,
  onDelete,
}: {
  job: PoseTransferJobResponse;
  onSelect?: (job: PoseTransferJobResponse) => void;
  onCancel?: (jobId: string, force?: boolean) => void;
  onDelete?: (jobId: string) => void;
}) {
  const isActive = job.status === 'pending' || job.status === 'processing';
  const isFailed = job.status === 'failed';

  return (
    <div
      className={`group relative rounded-xl overflow-hidden bg-[var(--bg-elevated)] border border-[var(--border-subtle)] transition-all ${
        job.status === 'completed' && onSelect ? 'cursor-pointer hover:border-[var(--accent)]' : ''
      }`}
      onClick={() => job.status === 'completed' && onSelect?.(job)}
    >
      <div className="aspect-square relative">
        {job.status === 'completed' && job.images[0] ? (
          <LazyImage
            src={api.getThumbnailUrl(job.images[0])}
            fallbackSrc={api.getImageUrl(job.images[0])}
            alt={job.prompt}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[var(--bg-primary)]">
            {isActive && (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-[var(--text-muted)]">
                  {job.status === 'pending' ? `Queue #${job.position ?? '?'}` : 'Processing'}
                </span>
              </div>
            )}
            {isFailed && (
              <div className="flex flex-col items-center gap-2 text-red-400 p-4">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span className="text-xs text-center">{job.error || 'Failed'}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {isActive && onCancel && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(job.job_id); }}
              className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {(job.status === 'completed' || isFailed) && onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(job.job_id); }}
              className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm truncate">{job.prompt}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {job.width}x{job.height} | Strength: {job.controlnet_strength}
        </p>
      </div>
    </div>
  );
}

// Image Picker Modal
function ImagePickerModal({
  images,
  onSelect,
  onClose,
}: {
  images: JobResponse[];
  onSelect: (job: JobResponse) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm animate-fadeIn flex items-center justify-center p-4">
      <div className="bg-[var(--bg-elevated)] rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h2 className="text-xl font-semibold">Select Image</h2>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-primary)] rounded-lg transition-all cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-[var(--text-muted)]">No images in gallery</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                Generate some images first
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {images.map((job) => (
                <button
                  key={job.job_id}
                  onClick={() => onSelect(job)}
                  className="aspect-square rounded-lg overflow-hidden bg-[var(--bg-primary)] hover:ring-2 hover:ring-[var(--accent)] transition-all cursor-pointer"
                >
                  {job.images[0] && (
                    <LazyImage
                      src={api.getThumbnailUrl(job.images[0])}
                      fallbackSrc={api.getImageUrl(job.images[0])}
                      alt={job.prompt}
                      className="w-full h-full"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Pose Transfer Result Modal
function PoseTransferResultModal({
  job,
  jobs,
  poses,
  onClose,
  onNavigate,
  onDelete,
}: {
  job: PoseTransferJobResponse;
  jobs: PoseTransferJobResponse[];
  poses: Pose[];
  onClose: () => void;
  onNavigate: (job: PoseTransferJobResponse) => void;
  onDelete: (jobId: string) => void;
}) {
  const currentIndex = jobs.findIndex(j => j.job_id === job.job_id);
  const prevJob = currentIndex > 0 ? jobs[currentIndex - 1] : null;
  const nextJob = currentIndex < jobs.length - 1 ? jobs[currentIndex + 1] : null;
  const pose = job.pose_id ? poses.find(p => p.id === job.pose_id) : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && prevJob) onNavigate(prevJob);
      else if (e.key === 'ArrowRight' && nextJob) onNavigate(nextJob);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onNavigate, prevJob, nextJob]);

  if (job.status !== 'completed' || job.images.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 animate-fadeIn flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white/40 text-sm">
            {currentIndex + 1} / {jobs.length}
          </span>
        </div>
        <p className="text-white/80 text-sm truncate max-w-xl">{job.prompt}</p>
        <div className="flex items-center gap-2">
          <a
            href={api.getImageUrl(job.images[0])}
            download
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Download"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
          <button
            onClick={() => onDelete(job.job_id)}
            className="p-2 text-white/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all cursor-pointer"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main content with three-way comparison */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {prevJob && (
          <button
            onClick={() => onNavigate(prevJob)}
            className="absolute left-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}

        {/* Comparison view */}
        <div className="flex items-center gap-4 p-8 max-w-full">
          {/* Source image */}
          {job.source_image && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-white/40 text-xs uppercase tracking-wider">Source</span>
              <img
                src={api.getImageUrl(job.source_image)}
                alt="Source"
                className="max-h-[60vh] max-w-[25vw] object-contain rounded-lg"
              />
            </div>
          )}

          {/* Arrow */}
          <div className="text-white/20">+</div>

          {/* Pose skeleton */}
          {pose && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-white/40 text-xs uppercase tracking-wider">Pose</span>
              <img
                src={api.getPoseImageUrl(pose.skeleton_image)}
                alt="Pose"
                className="max-h-[60vh] max-w-[25vw] object-contain rounded-lg"
              />
            </div>
          )}

          {/* Arrow */}
          <div className="text-white/20">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </div>

          {/* Result image */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-white/40 text-xs uppercase tracking-wider">Result</span>
            <img
              src={api.getImageUrl(job.images[0])}
              alt={job.prompt}
              className="max-h-[60vh] max-w-[30vw] object-contain rounded-lg"
            />
          </div>
        </div>

        {/* Next button */}
        {nextJob && (
          <button
            onClick={() => onNavigate(nextJob)}
            className="absolute right-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom info */}
      <div className="px-4 py-3 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center justify-center gap-6 text-sm text-white/60">
          <span>{job.width}x{job.height}</span>
          <span>Steps: {job.steps}</span>
          <span>CFG: {job.cfg}</span>
          <span>Pose Strength: {job.controlnet_strength}</span>
          {job.seed && <span>Seed: {job.seed}</span>}
        </div>
      </div>
    </div>
  );
}
